/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * Used by native mobile applications to initialize external user session and
 * redirect to the target location
 */

import * as React from 'react';
import { useSearchParams } from 'react-router';
import axios, { AxiosError } from 'axios';
import { addHours } from 'date-fns';

import { Alert, Spinner, SecurityHelper } from 'sarsaparilla';
import { hasValidRecAccount } from 'sarsaparilla/auth';

import { isSameOriginUri } from 'shared-ui/utils/redirectUri';

import { isValidGuestAuth, type GuestAuthAccount } from './mobileSession.utils';

export function MobileSessionHandoff() {
    const [searchParams] = useSearchParams();

    const [isError, setIsError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    const redirectToNext = (nextUri: string | null) => {
        if (!nextUri) return;
        // A full-page load rehydrates auth from localStorage 'recaccount'
        window.location.assign(nextUri);
    };

    const initUserSession = async () => {
        SecurityHelper.storeMobileNativeHandoff();

        const pNextUri = searchParams.get('next');
        const pGuest = searchParams.get('guest');

        if (!isSameOriginUri(pNextUri)) {
            setIsError(true);
            setErrorMessage('Malformed request - invalid next');
            return;
        }

        if (pGuest === 'true') {
            const guestAuth = SecurityHelper.getMobileNativeGuestAuth();
            if (guestAuth === null) {
                setIsError(true);
                setErrorMessage('Malformed request - missing authentication');
                return;
            }

            let decodedAuth: unknown;
            try {
                // guestAuth is base64 encoded json
                decodedAuth = JSON.parse(atob(guestAuth));
            } catch (error) {
                setIsError(true);
                setErrorMessage(`${error}`);
                return;
            }

            // run some validations on the injected auth structure
            if (!isValidGuestAuth(decodedAuth)) {
                setIsError(true);
                setErrorMessage('Malformed request - invalid guest authentication');
                return;
            }

            window.localStorage.setItem('recaccount', JSON.stringify(decodedAuth));

            if (window.dataLayer) {
                window.dataLayer.push({
                    userId: decodedAuth.account.account_id,
                });
            }
            redirectToNext(pNextUri);
            return;
        }

        if (!hasValidRecAccount()) {
            const accessToken = SecurityHelper.getMobileNativeAuthToken();

            if (accessToken === null) {
                setIsError(true);
                setErrorMessage('Malformed request - missing authentication');
                return;
            }

            try {
                const response = await axios.get<GuestAuthAccount>(
                    '/api/accounts/account',
                    {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    }
                );
                const account = response.data;
                // e.g. 2024-05-02T16:20:45.744Z (ISO 8601)
                const expiration = addHours(new Date(), 1).toISOString();
                window.localStorage.setItem(
                    'recaccount',
                    JSON.stringify({
                        access_token: accessToken,
                        account,
                        expiration,
                    })
                );

                if (window.dataLayer) {
                    window.dataLayer.push({
                        userId: account.account_id,
                    });
                }

                redirectToNext(pNextUri);
                return;
            } catch (error) {
                let message = `${error}`;
                if (error instanceof AxiosError) {
                    if (error?.response?.data?.error) {
                        message = `${error}: ${error.response.data.error}`;
                    }
                }
                setIsError(true);
                setErrorMessage(message);
                return;
            }
        }

        redirectToNext(pNextUri);
    };

    React.useEffect(() => {
        initUserSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isError) {
        return (
            <Alert type="error" heading="Error loading your account">
                <p>
                    There was an error logging into your account. Please go back and try
                    again or log out and log back in again into the application.
                </p>
                <p>{errorMessage}</p>
            </Alert>
        );
    }

    return <Spinner />;
}

// cspell:ignore recaccount
